/**
 * App shell — the actions the new top bar / drawer trigger, COPIED from the classic header.
 *
 * Plan: the app shell plan (2026-10-08) §3 — resume block in Documents/Admin-data-table-redesign.md §0
 *
 * ⛔ These are 5-line copies, NOT a refactor of Header.vue: the classic header stays exactly as it
 *    is (it is what every client sees). If a classic behaviour changes, change the copy here too —
 *    each function names the Header.vue method it mirrors.
 *
 * Every function takes the calling component (`vm`, for `$store` / `$router`) so AppShell can call
 * them with `this`, and specs can pass a plain `{ $store, $router }` object.
 */
import { maskState, setUserMask } from '@/utils/phiMaskState';
import { parsePatientProfileID, recordRecentPatient } from '@/utils/recentPatients';

function customerState(vm) {
  return (vm && vm.$store && vm.$store.state && vm.$store.state.customer) || {};
}

/**
 * `$router.push` that swallows NavigationDuplicated (tapping the page that is already open) and
 * warns on anything else. Returns the push promise (resolves either way).
 */
export function navigate(vm, to, label) {
  if (!vm || !vm.$router || !to) return Promise.resolve();
  const p = vm.$router.push(to);
  if (!p || typeof p.catch !== 'function') return Promise.resolve();
  return p.catch((err) => {
    if (err && err.name !== 'NavigationDuplicated') {
      console.warn(`[AppShell] could not open ${label || 'the page'}:`, err);
    }
  });
}

/** Header.vue `gotoPatient` — /app/patientprofile/<user.SiteID>-<PatNum>. */
export function gotoPatient(vm, patient) {
  const user = customerState(vm).user;
  if (!patient || patient.PatNum == null || !user) return Promise.resolve();
  return navigate(vm, `/app/patientprofile/${user.SiteID}-${patient.PatNum}`, 'the patient profile');
}

/** Header.vue `GoToSettings`. */
export function goToSettings(vm) {
  return navigate(vm, '/app/settings', 'Settings');
}

/** Header.vue `GoToMyAccount`. */
export function goToMyAccount(vm) {
  return navigate(vm, '/app/my-account', 'My account');
}

/** Header.vue `Logout` — the customer store does the rest (MSAL, storage, redirect). */
export function logout(vm) {
  return vm.$store.dispatch('customer/logout');
}

/**
 * Header.vue `refreshPatientsList` minus the spinner flag (the caller owns it). Resolves `true`
 * when the roster reloaded, `false` on failure — never rejects, like the classic try/catch.
 */
export function refreshPatients(vm) {
  const siteID = customerState(vm).siteID;
  let p;
  try {
    p = vm.$store.dispatch('customer/getPatients', { siteID });
  } catch (e) {
    return Promise.resolve(false);
  }
  return Promise.resolve(p).then(() => true, () => false);
}

/** Header.vue `phiMaskOn` — the per-user OR the site-wide demo mask. */
export function isMaskOn() {
  const s = maskState();
  return s.userMask === true || s.siteMask === true;
}

/**
 * Header.vue `togglePhiMask` — confirm, flip the per-user mask, then RELOAD. The reload is not
 * optional: the Vuex store still holds data fetched before the flip, and a half-masked screen is
 * the leak the mask exists to prevent. `deps` is for specs only. Returns true when it flipped.
 */
export function confirmToggleMask(vm, deps = {}) {
  const confirmFn = deps.confirm || ((msg) => window.confirm(msg));
  const reload = deps.reload || (() => window.location.reload());
  const next = !isMaskOn();
  const ok = confirmFn(next
    ? 'Turn the demo mask ON?\n\nPatient names, phone numbers, emails, addresses and DOB will be replaced with fake data for THIS browser session only. Outbound sends (SMS, email, campaigns, AI calls) will be blocked.\n\nThe page will reload.'
    : 'Turn the demo mask OFF?\n\nReal patient data will be shown again and sends will be re-enabled.\n\nThe page will reload.');
  if (!ok) return false;
  setUserMask(next);
  reload();
  return true;
}

/**
 * Header.vue `trackRecentPatient` — call from the shell's `$route` watcher (immediate). Returns the
 * new recent PatNums when `route` is a profile of the signed-in site, else null (keep yours).
 */
export function trackRecentPatient(siteID, route) {
  if (!route || route.name !== 'patientprofile' || !siteID) return null;
  const link = parsePatientProfileID(route.params && route.params.patientID);
  if (!link) return null;
  if (link.siteID.toLowerCase() !== String(siteID).toLowerCase()) return null;
  return recordRecentPatient(siteID, link.patNum);
}

/** Header.vue `currentSiteName` — store site row, then the login user row, then localStorage. */
export function currentSiteName(vm) {
  const st = customerState(vm);
  const getSite = vm && vm.$store && vm.$store.getters ? vm.$store.getters['customer/getSite'] : null;
  const site = typeof getSite === 'function' ? getSite(st.siteID) : null;
  const nameOf = (r) => (r && (r.SiteName || r.Name || r.name)) || null;
  if (site) return nameOf(site);
  if (st.user) return nameOf(st.user);
  try {
    return nameOf(JSON.parse(localStorage.getItem('user')));
  } catch (e) {
    return null;
  }
}

/** Header.vue `dateFormatStr` — the site's DateFormat, default MM/DD/YYYY. */
export function siteDateFormat(vm) {
  const st = customerState(vm);
  const getSite = vm && vm.$store && vm.$store.getters ? vm.$store.getters['customer/getSite'] : null;
  const site = typeof getSite === 'function' ? getSite(st.siteID) : null;
  return (site && site.DateFormat) || 'MM/DD/YYYY';
}

/** Header.vue `unreadConversationsCount` — sum of conversationsV2 rooms' unreadCount. */
export function unreadConversationsCount(rooms) {
  if (!Array.isArray(rooms)) return 0;
  return rooms.reduce((sum, room) => sum + (parseInt(room && room.unreadCount, 10) || 0), 0);
}

/** Header.vue `notificationCountDisplay` — above 10 the badge reads "10+". 0 / bad ⇒ ''. */
export function badgeText(n) {
  const v = Number(n) || 0;
  if (v <= 0) return '';
  return v > 10 ? '10+' : String(v);
}
